// src/contexts/AuthContext.jsx
import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '../lib/supabase';

// Create the authentication context
const AuthContext = createContext();

// Hook to use the auth context
export function useAuth() {
  return useContext(AuthContext);
}

// Provider component
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up the auth subscription
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event);
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // Initial session check
    const getInitialSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      console.log('Initial session:', session ? 'exists' : 'none');
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    };
    
    getInitialSession();

    // Cleanup subscription
    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  // Sign up with email and password
  const signUp = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      // Check for rate limiting
      const loginAttempts = checkLoginRateLimit(email);
      if (loginAttempts.blocked) {
        const waitTime = loginAttempts.waitTimeSeconds;
        throw new Error(`Too many login attempts. Please try again in ${Math.ceil(waitTime / 60)} minute${waitTime > 60 ? 's' : ''} and ${waitTime % 60} second${waitTime % 60 !== 1 ? 's' : ''}.`);
      }
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        // Record failed login attempt
        recordFailedLoginAttempt(email);
        throw error;
      }
      
      // Login successful, reset attempts
      resetLoginAttempts(email);
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };
  
  // Rate limiting helper functions
  const checkLoginRateLimit = (email) => {
    const now = new Date().getTime();
    const KEY_PREFIX = 'login_attempts_';
    const storageKey = `${KEY_PREFIX}${email}`;
    
    // Get stored attempts
    const storedData = localStorage.getItem(storageKey);
    if (!storedData) {
      return { blocked: false, attempts: 0 };
    }
    
    const data = JSON.parse(storedData);
    const attempts = data.attempts || 0;
    const timestamp = data.timestamp || 0;
    const timeDiff = now - timestamp;
    
    // Check if we're in a lockout period
    if (attempts >= 12) {
      // 5-minute lockout (300 seconds)
      const waitTimeSeconds = 300 - Math.floor(timeDiff / 1000);
      if (waitTimeSeconds > 0) {
        return { blocked: true, waitTimeSeconds };
      }
    } else if (attempts >= 6) {
      // 30-second lockout
      const waitTimeSeconds = 30 - Math.floor(timeDiff / 1000);
      if (waitTimeSeconds > 0) {
        return { blocked: true, waitTimeSeconds };
      }
    }
    
    return { blocked: false, attempts };
  };
  
  const recordFailedLoginAttempt = (email) => {
    const now = new Date().getTime();
    const KEY_PREFIX = 'login_attempts_';
    const storageKey = `${KEY_PREFIX}${email}`;
    
    // Get current attempts
    const storedData = localStorage.getItem(storageKey);
    let attempts = 1;
    
    if (storedData) {
      const data = JSON.parse(storedData);
      // If it's been over 24 hours, reset attempts
      if (now - data.timestamp > 24 * 60 * 60 * 1000) {
        attempts = 1;
      } else {
        attempts = (data.attempts || 0) + 1;
      }
    }
    
    // Store updated attempts
    localStorage.setItem(storageKey, JSON.stringify({
      attempts,
      timestamp: now
    }));
  };
  
  const resetLoginAttempts = (email) => {
    const KEY_PREFIX = 'login_attempts_';
    const storageKey = `${KEY_PREFIX}${email}`;
    localStorage.removeItem(storageKey);
  };

  // Sign out
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      // Get the full URL to the reset-password page
      const redirectUrl = `${window.location.origin}/reset-password`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });
      
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}